import React, {useMemo, useRef, useState} from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, {SelectOption} from '@amzn/meridian/select';
import {
    ALLOWED_COLUMN_LABELS,
    BUILDER_COLUMN_MODES,
    BUILDER_VALIDATION_CODES,
    buildRackDescription,
    createInitialColumnModes,
    cycleColumnMode,
    findConfigsByRackDescription
} from './AisleConfigModel';
import {RACK_ZONE_COLORS, STATUS_COLORS} from './AisleConfigColors';

// Shared with the rack preview so a painted column reads as the same colour in both views.
const modeStyles = {
    [BUILDER_COLUMN_MODES.ABSENT]: {...RACK_ZONE_COLORS.ABSENT},
    [BUILDER_COLUMN_MODES.BAG]: {...RACK_ZONE_COLORS.BAG},
    [BUILDER_COLUMN_MODES.OV]: {...RACK_ZONE_COLORS.OV},
    [BUILDER_COLUMN_MODES.FLEX]: {...RACK_ZONE_COLORS.FLEX}
};

const getMessage = (intl, id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);

const MODE_MESSAGES = {
    [BUILDER_COLUMN_MODES.ABSENT]: {id: 'aisleconfig.builder.mode.absent', defaultMessage: 'Not present'},
    [BUILDER_COLUMN_MODES.BAG]: {id: 'aisleconfig.builder.mode.bag', defaultMessage: 'Bag'},
    [BUILDER_COLUMN_MODES.OV]: {id: 'aisleconfig.builder.mode.ov', defaultMessage: 'OV'},
    [BUILDER_COLUMN_MODES.FLEX]: {id: 'aisleconfig.builder.mode.flex', defaultMessage: 'Flex'}
};

// The Clear chip erases a column, so it carries its own label instead of "Not present".
const PAINT_MODES = [
    {mode: BUILDER_COLUMN_MODES.BAG, message: MODE_MESSAGES[BUILDER_COLUMN_MODES.BAG]},
    {mode: BUILDER_COLUMN_MODES.OV, message: MODE_MESSAGES[BUILDER_COLUMN_MODES.OV]},
    {mode: BUILDER_COLUMN_MODES.FLEX, message: MODE_MESSAGES[BUILDER_COLUMN_MODES.FLEX]},
    {mode: BUILDER_COLUMN_MODES.ABSENT, message: {id: 'aisleconfig.builder.mode.clear', defaultMessage: 'Clear'}}
];

// Every machine-readable validation code from the model maps to a localizable message;
// the params supplied by the model interpolate into the ICU placeholders.
const ERROR_MESSAGES = {
    [BUILDER_VALIDATION_CODES.FLEX_PAIRS_REQUIRED]: {
        id: 'aisleconfig.builder.error.flexpairs',
        defaultMessage: 'Select Flex columns in adjacent pairs — every two columns form one physical footprint.'
    },
    [BUILDER_VALIDATION_CODES.FLEX_NOT_ADJACENT]: {
        id: 'aisleconfig.builder.error.flexadjacent',
        defaultMessage: 'Flex columns must be adjacent, with no Bag or OV column between them.'
    },
    [BUILDER_VALIDATION_CODES.PILLAR_INSIDE_FLEX_ZONE]: {
        id: 'aisleconfig.builder.error.flexpillar',
        defaultMessage: 'A pillar cannot sit inside the Flex zone (after column {columns}).'
    },
    [BUILDER_VALIDATION_CODES.FLEX_OV_LABELS_NOT_UNIQUE]: {
        id: 'aisleconfig.builder.error.flexlabelsunique',
        defaultMessage: 'Every flex OV label must be unique — each footprint needs its own top and bottom letters.'
    },
    [BUILDER_VALIDATION_CODES.FLEX_OV_LABEL_REUSED]: {
        id: 'aisleconfig.builder.error.flexlabelreused',
        defaultMessage: 'Flex OV labels cannot reuse a letter already selected as a Bag or OV column ({letters}).'
    },
    [BUILDER_VALIDATION_CODES.NO_COLUMNS_SELECTED]: {
        id: 'aisleconfig.builder.error.nocolumns',
        defaultMessage: 'Select at least one rack column.'
    }
};

const getErrorMessage = (intl, error) => {
    const definition = ERROR_MESSAGES[error.code] || {
        id: 'aisleconfig.builder.error.generic',
        defaultMessage: 'The current rack description is not valid.'
    };
    return getMessage(intl, definition.id, definition.defaultMessage, error.params);
};

const styles = {
    shell: {
        border: '1px solid #d9e2e8',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 8px rgba(15, 41, 61, 0.08)',
        overflow: 'hidden'
    },
    header: {
        background: 'linear-gradient(120deg, #0b4d74 0%, #007eb9 100%)',
        padding: '18px 22px',
        color: '#ffffff'
    },
    headerTitle: {margin: 0, fontSize: '20px', fontWeight: 700, color: '#ffffff'},
    headerSubtitle: {margin: '4px 0 0', fontSize: '13px', color: '#d6ecf7'},
    headerControlLabel: {
        display: 'block',
        marginBottom: '2px',
        fontSize: '11px',
        fontWeight: 700,
        color: '#d6ecf7',
        textTransform: 'uppercase',
        letterSpacing: '0.4px'
    },
    body: {padding: '20px', display: 'grid', gap: '16px'},
    section: {
        border: '1px solid #e4ebf0',
        borderRadius: '10px',
        padding: '16px',
        backgroundColor: '#ffffff'
    },
    previewSection: {
        border: '1px solid #cfe3ef',
        borderRadius: '10px',
        padding: '16px',
        backgroundColor: '#f7fbfd'
    },
    sectionTitle: {display: 'flex', alignItems: 'center', fontSize: '15px', fontWeight: 700, color: '#16191f'},
    hint: {margin: '6px 0 0', fontSize: '12px', color: '#5f6b7a'},
    paintBar: {display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '12px'},
    paintLabel: {fontSize: '11px', fontWeight: 700, color: '#5f6b7a', textTransform: 'uppercase', letterSpacing: '0.4px'},
    paintChip: (mode, selected) => ({
        padding: '5px 14px',
        borderRadius: '999px',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: modeStyles[mode].borderColor,
        backgroundColor: modeStyles[mode].backgroundColor,
        color: modeStyles[mode].color,
        fontSize: '12px',
        fontWeight: selected ? 800 : 600,
        cursor: 'pointer',
        boxShadow: selected ? '0 0 0 2px rgba(0, 126, 185, 0.35)' : 'none'
    }),
    hintInline: {fontSize: '11px', color: '#5f6b7a'},
    columnStrip: {display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: '2px', marginTop: '12px'},
    columnButton: {
        width: '72px',
        borderStyle: 'solid',
        borderWidth: '2px',
        borderRadius: '8px',
        minHeight: '60px',
        cursor: 'pointer',
        fontWeight: 700,
        transition: 'transform 80ms ease, box-shadow 80ms ease'
    },
    columnLetter: {fontSize: '17px', lineHeight: '20px'},
    columnCaption: {fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px'},
    pillarSlot: (active, enabled) => ({
        width: '14px',
        minHeight: '60px',
        padding: 0,
        borderRadius: '4px',
        border: active ? '1px solid #5f6b7a' : '1px dashed #c4cfd8',
        backgroundColor: active ? '#5f6b7a' :
            (enabled ? RACK_ZONE_COLORS.ABSENT.backgroundColor : 'transparent'),
        color: '#ffffff',
        cursor: enabled ? 'pointer' : 'default',
        opacity: enabled ? 1 : 0.3,
        fontSize: '10px'
    }),
    matchChip: (hasMatches) => {
        const status = hasMatches ? STATUS_COLORS.MATCHED : STATUS_COLORS.UNMATCHED;
        return {
            padding: '4px 12px',
            borderRadius: '999px',
            border: `1px solid ${status.borderColor}`,
            backgroundColor: status.backgroundColor,
            color: status.color,
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer'
        };
    },
    derivedGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '10px',
        marginTop: '10px'
    },
    derivedItem: {
        border: '1px solid #e4ebf0',
        borderLeft: '3px solid #007eb9',
        borderRadius: '6px',
        padding: '10px 12px',
        backgroundColor: '#ffffff'
    },
    derivedLabel: {fontSize: '11px', fontWeight: 700, color: '#5f6b7a', textTransform: 'uppercase', letterSpacing: '0.4px'},
    code: {
        display: 'block',
        marginTop: '4px',
        fontSize: '13px',
        overflowWrap: 'anywhere'
    },
    candidateName: {
        display: 'block',
        marginTop: '4px',
        padding: '10px 12px',
        borderRadius: '6px',
        backgroundColor: '#0b4d74',
        color: '#eaf6fc',
        fontSize: '13px',
        overflowWrap: 'anywhere'
    },
    copyButton: {
        padding: '3px 12px',
        borderRadius: '999px',
        border: '1px solid #007eb9',
        backgroundColor: '#ffffff',
        color: '#007eb9',
        fontSize: '11px',
        fontWeight: 700,
        cursor: 'pointer'
    },
    confirmation: {fontSize: '12px', fontWeight: 700, color: STATUS_COLORS.MATCHED.color}
};

const getModeLabel = (intl, mode) => {
    const message = MODE_MESSAGES[mode];
    return getMessage(intl, message.id, message.defaultMessage);
};

export const VisualRackBuilder = ({configs, intl, RackPreviewComponent, onSelectConfig}) => {
    const [region, setRegion] = useState('NA');
    const [columnModes, setColumnModes] = useState(createInitialColumnModes);
    const [pillars, setPillars] = useState([]);
    const [flexOvOverrides, setFlexOvOverrides] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState('');
    const [confirmedMatch, setConfirmedMatch] = useState('');
    const [paintMode, setPaintMode] = useState(null);
    const [isPainting, setIsPainting] = useState(false);
    const [candidateCopyStatus, setCandidateCopyStatus] = useState('');
    const matchesRef = useRef(null);

    const description = useMemo(() => buildRackDescription({
        region,
        columnModes,
        pillars,
        flexOvLabels: flexOvOverrides
    }), [region, columnModes, pillars, flexOvOverrides]);

    const matches = useMemo(() => findConfigsByRackDescription(configs, description), [configs, description]);
    const hasColumns = description.columns.length > 0;
    const hasErrors = description.errors.length > 0;

    const resetMatchSelection = () => {
        setSelectedMatch('');
        setConfirmedMatch('');
    };

    const applyColumnMode = (label, nextMode) => {
        const currentMode = columnModes[label];
        if (currentMode !== nextMode &&
            (currentMode === BUILDER_COLUMN_MODES.FLEX || nextMode === BUILDER_COLUMN_MODES.FLEX)) {
            setFlexOvOverrides([]);
        }
        setColumnModes((current) => ({...current, [label]: nextMode}));
        resetMatchSelection();
    };

    const handleColumnClick = (label) => {
        applyColumnMode(label, paintMode !== null ? paintMode : cycleColumnMode(columnModes[label]));
    };

    const togglePillar = (label) => {
        setPillars((current) => current.includes(label) ?
            current.filter((value) => value !== label) : [...current, label]);
        resetMatchSelection();
    };

    const setFootprintOvLabel = (footprintIndex, slot, letter) => {
        const base = description.footprints[footprintIndex] ?
            [...description.footprints[footprintIndex].ovLabels] : ['T', 'U'];
        base[slot] = letter;
        setFlexOvOverrides((current) => {
            const next = [...current];
            next[footprintIndex] = base;
            return next;
        });
        resetMatchSelection();
    };

    const resetBuilder = () => {
        setColumnModes(createInitialColumnModes());
        setPillars([]);
        setFlexOvOverrides([]);
        setPaintMode(null);
        setIsPainting(false);
        setCandidateCopyStatus('');
        resetMatchSelection();
    };

    const copyCandidateName = async () => {
        setCandidateCopyStatus('');
        const unavailableMessage = getMessage(intl, 'aisleconfig.copy.unavailable',
            'Clipboard access is unavailable. Select the config name and copy it manually.');
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(description.candidateName);
                setCandidateCopyStatus(getMessage(intl, 'aisleconfig.copy.success', 'Config name copied.'));
            } catch (error) {
                setCandidateCopyStatus(unavailableMessage);
            }
        } else {
            setCandidateCopyStatus(unavailableMessage);
        }
    };

    const scrollToMatches = () => {
        if (matchesRef.current && typeof matchesRef.current.scrollIntoView === 'function') {
            matchesRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    };

    return (
        <div style={styles.shell} data-testid="visual-rack-builder">
            <div style={styles.header}>
                <Row widths={["fill", "fit", "fit"]} alignmentVertical="center" spacing="300">
                    <div>
                        <p style={styles.headerTitle}>
                            {getMessage(intl, 'aisleconfig.builder.title', 'SBL Aisle Config Builder')}
                        </p>
                        <p style={styles.headerSubtitle}>
                            {getMessage(intl, 'aisleconfig.builder.subtitle',
                                'Describe the physical rack — the aisle code, config name, and matching ' +
                                'existing configs are derived for you. No naming knowledge needed.')}
                        </p>
                    </div>
                    <div>
                        <span style={styles.headerControlLabel}>
                            {getMessage(intl, 'aisleconfig.builder.region', 'Region')}
                        </span>
                        <Select id="rack-region" value={region} width={90} onChange={(value) => {
                            setRegion(value);
                            resetMatchSelection();
                        }}>
                            {['NA', 'EU', 'FE'].map((value) =>
                                <SelectOption key={value} value={value} label={value}/>)}
                        </Select>
                    </div>
                    <Button size="small" type="secondary" onClick={resetBuilder}>
                        {getMessage(intl, 'aisleconfig.builder.reset', 'Reset')}
                    </Button>
                </Row>
            </div>

            <div style={styles.body}>
                <div style={styles.section} data-testid="column-selection-section">
                    <div style={styles.sectionTitle}>
                        {getMessage(intl, 'aisleconfig.builder.selectcolumns', 'Select Bag, OV, and Flex columns')}
                    </div>
                    <p style={styles.hint}>
                        {getMessage(intl, 'aisleconfig.builder.selecthint',
                            'Pick a paint mode and click or drag across columns to apply it — or leave paint ' +
                            'off to cycle a column per click. Click the thin gap after a column to toggle a ' +
                            'physical pillar. Every two adjacent Flex columns form one footprint — pick its ' +
                            'top and bottom OV labels directly in the preview below.')}
                    </p>
                    <div style={styles.paintBar} data-testid="paint-mode-bar">
                        <span style={styles.paintLabel}>
                            {getMessage(intl, 'aisleconfig.builder.paintmode', 'Paint mode')}
                        </span>
                        {PAINT_MODES.map(({mode, message}) => (
                            <button key={`paint-${mode}`}
                                    type="button"
                                    data-testid={`paint-mode-${mode}`}
                                    aria-pressed={paintMode === mode}
                                    style={styles.paintChip(mode, paintMode === mode)}
                                    onClick={() => {
                                        setPaintMode(paintMode === mode ? null : mode);
                                        setIsPainting(false);
                                    }}>
                                {getMessage(intl, message.id, message.defaultMessage)}
                            </button>
                        ))}
                        <span style={styles.hintInline}>
                            {paintMode === null ?
                                getMessage(intl, 'aisleconfig.builder.paintoff',
                                    'Paint off — clicking a column cycles its type.') :
                                getMessage(intl, 'aisleconfig.builder.painton',
                                    'Click or drag across columns to apply.')}
                        </span>
                    </div>
                    <div style={styles.columnStrip}
                         onMouseUp={() => setIsPainting(false)}
                         onMouseLeave={() => setIsPainting(false)}>
                        {ALLOWED_COLUMN_LABELS.map((label) => {
                            const mode = columnModes[label];
                            const active = mode !== BUILDER_COLUMN_MODES.ABSENT;
                            return (
                                <React.Fragment key={label}>
                                    <button type="button"
                                            aria-label={getMessage(intl, 'aisleconfig.builder.columnaria',
                                                'Column {label}: {mode}',
                                                {label, mode: getModeLabel(intl, mode)})}
                                            data-testid={`rack-column-${label}`}
                                            style={{...styles.columnButton, ...modeStyles[mode]}}
                                            onMouseDown={() => {
                                                if (paintMode !== null) {
                                                    setIsPainting(true);
                                                    handleColumnClick(label);
                                                }
                                            }}
                                            onMouseEnter={() => {
                                                if (isPainting && paintMode !== null) {
                                                    handleColumnClick(label);
                                                }
                                            }}
                                            onClick={() => {
                                                // Always apply on click: keyboard activation paints
                                                // too, and a re-apply after mousedown is a no-op.
                                                handleColumnClick(label);
                                            }}>
                                        <div style={styles.columnLetter}>
                                            {mode === BUILDER_COLUMN_MODES.OV ? label.toUpperCase() : label}
                                        </div>
                                        <div style={styles.columnCaption}>{getModeLabel(intl, mode)}</div>
                                    </button>
                                    <button type="button"
                                            data-testid={`pillar-slot-${label}`}
                                            aria-label={getMessage(intl, 'aisleconfig.builder.pillararia',
                                                'Pillar after column {label}: {state}', {
                                                    label,
                                                    state: pillars.includes(label) ?
                                                        getMessage(intl, 'aisleconfig.builder.pillar.present', 'present') :
                                                        getMessage(intl, 'aisleconfig.builder.pillar.absent', 'absent')
                                                })}
                                            title={getMessage(intl, 'aisleconfig.builder.pillartitle',
                                                'Toggle a pillar (physical gap) after this column')}
                                            disabled={!active}
                                            style={styles.pillarSlot(pillars.includes(label), active)}
                                            onClick={() => togglePillar(label)}>
                                        {pillars.includes(label) ? '▮' : ''}
                                    </button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                <div style={styles.previewSection} data-testid="rack-preview-section">
                    <Row widths={["fill", "fit"]} alignmentVertical="center" spacing="300">
                        <div style={styles.sectionTitle}>
                            {getMessage(intl, 'aisleconfig.builder.livepreview', 'Live rack preview')}
                        </div>
                        {hasColumns && !hasErrors &&
                            <button type="button"
                                    data-testid="match-count-chip"
                                    style={styles.matchChip(matches.length > 0)}
                                    title={getMessage(intl, 'aisleconfig.builder.matchchiptitle',
                                        'Jump to the matching existing configs')}
                                    onClick={scrollToMatches}>
                                {matches.length === 1 ?
                                    getMessage(intl, 'aisleconfig.builder.matchchip.one',
                                        '{count} existing config matches', {count: matches.length}) :
                                    getMessage(intl, 'aisleconfig.builder.matchchip.many',
                                        '{count} existing configs match', {count: matches.length})}
                            </button>}
                    </Row>
                    {!hasColumns ?
                        <Alert type="informational">
                            {getMessage(intl, 'aisleconfig.builder.emptypreview',
                                'Select a column above to preview the physical rack.')}
                        </Alert> :
                        <React.Fragment>
                            {hasErrors &&
                                <Alert type="warning">
                                    {description.errors.map((error) =>
                                        <div key={error.code}>{getErrorMessage(intl, error)}</div>)}
                                </Alert>}
                            <div style={{marginTop: '10px'}}>
                                <RackPreviewComponent columns={description.columns}
                                                      intl={intl}
                                                      onFlexOvLabelChange={setFootprintOvLabel}/>
                            </div>
                            <div style={styles.derivedGrid}>
                                <div style={styles.derivedItem}>
                                    <span style={styles.derivedLabel}>
                                        {getMessage(intl, 'aisleconfig.builder.aislecode', 'Aisle code')}
                                    </span>
                                    <code style={styles.code} data-testid="derived-aisle-code">{description.aisleCode}</code>
                                </div>
                                {description.bagCode && description.ovCode &&
                                    <div style={styles.derivedItem}>
                                        <span style={styles.derivedLabel}>
                                            {getMessage(intl, 'aisleconfig.builder.ovcode', 'OV-mode code')}
                                        </span>
                                        <code style={styles.code} data-testid="derived-ov-code">{description.ovCode}</code>
                                    </div>}
                            </div>
                            <div style={{marginTop: '10px'}}>
                                <Row widths={["fill", "fit"]} alignmentVertical="center" spacing="300">
                                    <span style={styles.derivedLabel}>
                                        {getMessage(intl, 'aisleconfig.builder.derivedname',
                                            'Derived config name (year auto-stamped; matching ignores year)')}
                                    </span>
                                    <button type="button"
                                            data-testid="copy-candidate-name"
                                            style={styles.copyButton}
                                            onClick={copyCandidateName}>
                                        {getMessage(intl, 'aisleconfig.builder.copy', 'Copy')}
                                    </button>
                                </Row>
                                <code style={styles.candidateName} data-testid="candidate-config-name">
                                    {description.candidateName}
                                </code>
                                {candidateCopyStatus &&
                                    <div style={{marginTop: '6px'}}>
                                        <Alert type="informational" size="small">{candidateCopyStatus}</Alert>
                                    </div>}
                            </div>
                            <div style={{marginTop: '10px'}}>
                                <Alert type="informational" size="small">
                                    {getMessage(intl, 'aisleconfig.builder.previewnote',
                                        'This preview is derived from your description. Authoritative LED/bin ' +
                                        'mappings from the S3 JSON will appear once the read-only NDMS ' +
                                        'config-content operation is available.')}
                                </Alert>
                            </div>
                        </React.Fragment>}
                </div>

                {!hasErrors && hasColumns &&
                    <div style={styles.section} data-testid="matches-section" ref={matchesRef}>
                        <div style={styles.sectionTitle}>
                            {getMessage(intl, 'aisleconfig.builder.matchestitle',
                                'Existing structural matches: {count}', {count: matches.length})}
                        </div>
                        {matches.length === 0 ?
                            <div style={{marginTop: '10px'}}>
                                <Alert type="warning">
                                    {getMessage(intl, 'aisleconfig.builder.nomatches',
                                        'No existing S3 config name matches this physical structure (any year).')}
                                </Alert>
                            </div> :
                            <Column spacing="200" spacingInset="300 none none none">
                                <Select id="structural-match"
                                        value={selectedMatch}
                                        onChange={(value) => {
                                            setSelectedMatch(value);
                                            setConfirmedMatch('');
                                        }}
                                        placeholder={getMessage(intl, 'aisleconfig.builder.matchplaceholder',
                                            'Select a matching existing config')}>
                                    {matches.map((name) => <SelectOption key={name} value={name} label={name}/>)}
                                </Select>
                                <Row alignmentVertical="center" spacing="300">
                                    <Button type="primary"
                                            size="small"
                                            disabled={!selectedMatch}
                                            onClick={() => {
                                                setConfirmedMatch(selectedMatch);
                                                onSelectConfig(selectedMatch);
                                            }}>
                                        {getMessage(intl, 'aisleconfig.builder.usematch', 'Use matched config')}
                                    </Button>
                                    {confirmedMatch &&
                                        <span data-testid="match-selection-confirmation" style={styles.confirmation}>
                                            {getMessage(intl, 'aisleconfig.builder.matchselected',
                                                '✓ Selected — full details below')}
                                        </span>}
                                </Row>
                            </Column>}
                    </div>}
            </div>
        </div>
    );
};

export default VisualRackBuilder;



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/aisleconfig/VisualRackBuilder.js